/* eslint-disable react/destructuring-assignment */
import React from 'react';
import {useMatomo} from '@datapunt/matomo-tracker-react';
import {Button, DropdownItem, DropdownToggle} from 'reactstrap';
import {NavLink} from 'react-router-dom';

export const TRACKING_NAMES = {
  KITU_HOME_PAGE: 'Kitu.io Home Page',
  HOME_PAGE: 'Home Page',
  ACTIVATE_PAGE: 'Activate Page',
  ERROR_PAGE: 'Error Page',
  REGISTER_PAGE: 'Register Page',
  LOGIN_PAGE: 'Login Page',
  SUPPORT_PAGE: 'Support Page',
  CAROUSEL: 'Carousel Component',
  DASHBOARD: 'Dashboard Component',
  SITE_LIST: 'Site List',
  STATIONS_LIST: 'Stations List',
  WIZARD: 'Wizard',
  NAVIGATION: 'Navigation',
  SIDEBAR: 'Sidebar',
  FOOTER: 'Footer',
  NOTIFICATIONS: 'Notification Component',
  UNAUTHORIZED_PAGE: 'Unauthorized Page',
  FAQ_PAGE: 'FAQ Page',
  FORGOT_PASSWORD_PAGE: 'Forgot Password Page',
  RESET_PASSWORD_PAGE: 'Reset Password Page',
  PROFILE_PAGE: 'Profile Page',
  BILLING_FORM: 'Billing Form',
  USER_INFO_FORM: 'User Info Form',
  CHANGE_PASSWORD_FORM: 'Change Password Form',
  VEHICLE_FORM: 'Vehicle Form',
  PUBLIC_PAYMENT_WIZARD: 'Public Payment Wizard',
  PROMPT_MODAL: 'Prompt Modal',
  TEXT_NOTIFICATION_FORM: 'Text Notification Form',
};

export const TRACKING_ACTIONS = {
  ON_CLICK: 'on Click',
  ON_NAVIGATE: 'on Navigate',
  ON_TOGGLE_SIDEBAR: 'on toggle Sidebar',
  ON_CLICK_LEFT: 'on Click Left', // Carousel or wizard
  ON_CLICK_RIGHT: 'on Click Right', // Carousel or wizard
  ON_SITE_DROPDOWN_ITEM_SELECT: 'on Site Dropdown Item select',
  ON_SITE_DROPDOWN_SELECT: 'on Site Dropdown select',
  ON_STATION_SELECT: 'on Station select',
  ON_LANGUAGE_DROPDOWN_ITEM_SELECT: 'on Language Dropdown Item select',
  ON_LANGUAGE_DROPDOWN_SELECT: 'on Language Dropdown select',
  ON_PROFILE_DROPDOWN_ITEM_SELECT: 'on Profile Dropdown Item select',
  ON_PROFILE_DROPDOWN_SELECT: 'on Profile Dropdown select',
  ON_NOTIFICATION_DROPDOWN_SELECT: 'on Notification Dropdown select',
  ON_NOTIFICATION_DROPDOWN_ITEM_SELECT: 'on Notification Dropdown Item select',
  ON_START_CHARGING_SELECT: 'on Start Charging select',
  ON_FAQ_ITEM_SELECT: 'on FAQ Item select',
  ON_CLICK_START_CHARING: 'on click Start Charging',
  ON_CLICK_PAY_PAST_DUE: 'on Click Pay Past Due',
  ON_CLICK_CLOSE_MODAL: 'on Click Close Modal',
  ON_FORM_SUBMIT: 'on Form Submit', // Forgot password, Register, Activate, etc.
};

/**
 * Used in conjuction with a 'name'. If multiple category/action/names exist,
 * this can help distinguish
 */
export const TRACKING_NAVIGATION_OPTIONS = {
  VIA_LOGO: 'via Logo',
  VIA_BUTTON: 'via Button',
  VIA_TEXT: 'via Text',
  VIA_ICON: 'via Icon',
};

/**
 * Used when the 'name' should be more specific
 */
const specialNames = {
  [TRACKING_ACTIONS.ON_NAVIGATE]: name => `Navigate to: ${name}`,
  [TRACKING_ACTIONS.ON_SITE_DROPDOWN_ITEM_SELECT]: name => `Site Dropdown Select: ${name}`,
  [TRACKING_ACTIONS.ON_STATION_SELECT]: name => `Station Select: ${name}`,
  [TRACKING_ACTIONS.ON_LANGUAGE_DROPDOWN_ITEM_SELECT]: name => `Language Dropdown Select: ${name}`,
  [TRACKING_ACTIONS.ON_PROFILE_DROPDOWN_ITEM_SELECT]: name => `Profile Dropdown Select ${name}`,
  [TRACKING_ACTIONS.ON_FAQ_ITEM_SELECT]: name => `FAQ Item Select ${name}`,
};

/**
 * TrackedClickComponent - Adds tracking function onto an onClick method
 * before performing its usual onClick functionality.
 * @param {*} props - any props from the parent component
 * @param {*} Component - the component, default is html element <button>
 */
const TrackedOnClickComponent = (props, Component) => {
  const {trackEvent} = useMatomo();
  const {children} = props;
  const handleOnClick = e => {
    trackEvent(props.tracking);
    if (props.onClick) {
      props.onClick(e);
    }
  };

  return (
    Component ? (
      <Component {...props} onClick={e => handleOnClick(e)}>
        {children}
      </Component>
    ) : (
      // eslint-disable-next-line react/button-has-type
      <button {...props} onClick={e => handleOnClick(e)}>
        {children}
      </button>
    )
  );
};

/**
 * createTracking - Creates a tracking event request body for matomo analytics
 * @param {string} category - the category. Generally the page or the main component
 *  (e.g. 'Support Page' or 'Station List')
 * @param {string} action - the action taken. Usually prefaced with 'on'. (e.g. onClick,
 *  onStationSelect)
 * @param {string} [name] - When helpful, add a name. (e.g. Station Selection: North
 *  Lot - 2001)
 * @param {string} [value] - Rarely used, must be numeric value
 * @returns {Object} - Matomo tracking request body
 */
export const createTracking = (category, action, name, value) => {
  if (!category || !action) {
    throw new Error('Matomo tracking requires a category and action');
  }
  const trackingBody = {
    category,
    action,
  };
  if (name) {
    const concatName = Array.isArray(name) ? name.join(', ') : name;
    // eslint-disable-next-line no-prototype-builtins
    if (specialNames.hasOwnProperty(action)) {
      const specialNameFunc = specialNames[action];
      trackingBody.name = specialNameFunc(concatName);
    } else {
      trackingBody.name = concatName;
    }
  }
  if (value) {
    trackingBody.value = value;
  }
  return trackingBody;
};

export const TrackedDropdownToggle = props => TrackedOnClickComponent(props, DropdownToggle);

export const TrackedDropdownItem = props => TrackedOnClickComponent(props, DropdownItem);

export const TrackedNavLink = props => TrackedOnClickComponent(props, NavLink);

export const TrackedStyledButton = props => TrackedOnClickComponent(props, Button);

export const TrackedButton = props => TrackedOnClickComponent(props);
